import React, { useCallback } from 'react';
import {
  DxTextInput,
  DxRadioButtonGroup,
  DxRadioButton,
  DxButton,
} from '@dvag/design-system-react';
import { useTranslation } from 'react-i18next';
import { RefCallBack, RegisterOptions } from 'react-hook-form';

import { ChartView, FondCollectionItem } from '../types';
import { FundsCombobox } from './FundsCombobox';
import { FondFieldType } from 'utils/fieldList';
import './FondDetail.css';
import { checkIcon } from 'utils/util';
import { SavedFundDto } from 'hooks/useFundsAutosave';

export interface Props {
  allFunds: FondCollectionItem[];
  removeCurrentFund: () => void;
  disabledIsins?: string[];
  setAutosaveValue: <K extends keyof SavedFundDto>(
    field: K,
    value: SavedFundDto[K],
    resourceIndex: number,
  ) => void;
  getValues: (fieldName: string, resourceIndex?: number) => string | undefined;
  resourceIndex: number;
  register: <T1>(
    fieldName: string,
    resourceIndex?: number,
    options?: RegisterOptions,
  ) => {
    onValueChange: (e: { detail: unknown }) => void;
    errormessage: string;
    value: T1;
    resourceFieldName: string;
    ref: RefCallBack;
  };
}

export const FondDetail = ({
  allFunds,
  removeCurrentFund,
  disabledIsins = [],
  setAutosaveValue,
  resourceIndex,
  register,
  getValues,
}: Props) => {
  const { t } = useTranslation();

  const handleFundSelect = useCallback(
    ({ fondName, isin }: { fondName: string; isin: string }) => {
      setAutosaveValue(FondFieldType.fondName, fondName, resourceIndex);
      setAutosaveValue(FondFieldType.isin, isin, resourceIndex);
      const nextFond = getValues(FondFieldType.chartView, resourceIndex + 1);
      if (nextFond) {
        setAutosaveValue(FondFieldType.orderPreviousItem, isin, resourceIndex + 1);
      }
      if ((!fondName || !isin) && getValues('id')) {
        setAutosaveValue('id', undefined, resourceIndex);
      }
    },
    [setAutosaveValue, resourceIndex],
  );

  const selectedFund = getValues(FondFieldType.isin, resourceIndex)
    ? {
        fondName: getValues(FondFieldType.fondName, resourceIndex),
        label: getValues(FondFieldType.fondName, resourceIndex),
        isin: getValues(FondFieldType.isin, resourceIndex),
      }
    : undefined;

  return (
    <div id="fond-detail" className="fond-detail-container">
      <FundsCombobox
        id="fond-name-combobox"
        allFunds={allFunds}
        selectedFund={selectedFund}
        label={t('fundsModal.fondName')}
        disabledIsins={disabledIsins}
        onFundSelect={handleFundSelect}
        resourceIndex={resourceIndex}
        register={register}
      />

      <DxTextInput
        required
        {...register(FondFieldType.isin, resourceIndex)}
        id="fond-isin"
        label={t('fundsModal.isin')}
        readonly
      />

      <DxRadioButtonGroup
        id="chart-view-group"
        data-testid="chart-view-group"
        groupname="chartview"
        label={t('fundsModal.chartView')}
        {...register(FondFieldType.chartView, resourceIndex)}
      >
        <DxRadioButton
          id="chart-view-comparison"
          data-testid="chart-view-comparison"
          label={t('fundsModal.viewModeComparison')}
          value={ChartView.Comparison}
        />
        <DxRadioButton
          id="chart-view-chart"
          data-testid="chart-view-chart"
          label={t('fundsModal.viewModeChart')}
          value={ChartView.Chart}
        />
        <DxRadioButton
          id="chart-view-calculator"
          data-testid="chart-view-calculator"
          label={t('fundsModal.viewModeCalculator')}
          value={ChartView.Calculator}
        />
      </DxRadioButtonGroup>
      <DxButton
        data-testid="funds-delete-btn"
        id="funds-delete-btn"
        className="funds-delete-btn"
        onClick={() => removeCurrentFund()}
        icon={checkIcon('minus-kreis')}
        iconposition="right"
        type="text"
        stretch
        theme="destructive"
        label={t('fundsModal.cancel')}
      />
    </div>
  );
};
